import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Search, Building2, Users, Home, FileSignature, Receipt, Clock, X, Wrench, Package, Wallet, CreditCard, Car, Cog, ClipboardList, Store, FileBarChart, LayoutDashboard, Settings, Gauge, Gift, Tag } from 'lucide-react'
import { useSearchStore } from '@/stores'
import { useGlobalSearch } from '@/hooks/useSearch'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import type { SearchResult } from '@/types'
import { Spinner } from '@/components/ui/Spinner'
import { Button } from '@/components/ui/Button'

const typeIcons: Record<string, React.ReactNode> = {
  property: <Building2 className="w-4 h-4" />,
  customer: <Users className="w-4 h-4" />,
  unit: <Home className="w-4 h-4" />,
  lease: <FileSignature className="w-4 h-4" />,
  invoice: <Receipt className="w-4 h-4" />,
  maintenance: <Wrench className="w-4 h-4" />,
  parcel: <Package className="w-4 h-4" />,
  expense: <Wallet className="w-4 h-4" />,
  payment: <CreditCard className="w-4 h-4" />,
  vehicle: <Car className="w-4 h-4" />,
  equipment: <Cog className="w-4 h-4" />,
  work_order: <ClipboardList className="w-4 h-4" />,
  vendor: <Store className="w-4 h-4" />,
}

export function CommandPalette() {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    isOpen,
    query,
    recentSearches,
    selectedIndex,
    close,
    setQuery,
    setSelectedIndex,
    addRecentSearch,
  } = useSearchStore()

  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(timer)
  }, [query])

  const { data, isLoading } = useGlobalSearch(debouncedQuery)
  const results = data?.results || []

  // EP-74 incentives are optional dual-gated modules. Mirror the Sidebar gate so the
  // palette never surfaces a dead link to an /incentives/* route that App.tsx has not
  // registered (both are gated on the SAME Unleash flag + per-account setting).
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()
  const showIncentivesRewards =
    featureFlags.loyaltyPoints && (accountSettings?.settings?.loyalty_points_enabled ?? false)
  const showIncentivesAny =
    showIncentivesRewards ||
    (featureFlags.renewalBenefits && (accountSettings?.settings?.renewal_benefits_enabled ?? false))

  // QA B-008: the palette was entity-search only, so pages (e.g. Reports) were
  // unreachable — typing "reports" / "รายงาน" returned nothing. Add static page
  // destinations, matched against BOTH the current-language and English label so
  // bilingual users find them either way. The Incentives pages (EP-74) live below the
  // sidebar fold, so the palette is the reliable way to reach them on any viewport —
  // included only when the module is enabled (see the dual-gate above).
  const tEn = typeof i18n?.getFixedT === 'function' ? i18n.getFixedT('en', 'common') : t
  const STATIC_PAGES = [
    { key: 'dashboard', labelKey: 'nav.dashboard', path: '/', icon: <LayoutDashboard className="w-4 h-4" /> },
    { key: 'reports', labelKey: 'nav.reports', path: '/reports', icon: <FileBarChart className="w-4 h-4" /> },
    { key: 'properties', labelKey: 'nav.properties', path: '/properties', icon: <Building2 className="w-4 h-4" /> },
    { key: 'customers', labelKey: 'nav.customers', path: '/customers', icon: <Users className="w-4 h-4" /> },
    { key: 'leases', labelKey: 'nav.leases', path: '/leases', icon: <FileSignature className="w-4 h-4" /> },
    { key: 'invoices', labelKey: 'nav.invoices', path: '/invoices', icon: <Receipt className="w-4 h-4" /> },
    { key: 'payments', labelKey: 'nav.payments', path: '/payments', icon: <CreditCard className="w-4 h-4" /> },
    { key: 'expenses', labelKey: 'nav.expenses', path: '/expenses', icon: <Wallet className="w-4 h-4" /> },
    { key: 'maintenance', labelKey: 'nav.maintenance', path: '/maintenances', icon: <Wrench className="w-4 h-4" /> },
    { key: 'parcels', labelKey: 'nav.parcels', path: '/parcels', icon: <Package className="w-4 h-4" /> },
    { key: 'meters', labelKey: 'nav.meters', path: '/meters', icon: <Gauge className="w-4 h-4" /> },
    ...(showIncentivesRewards
      ? [
          { key: 'incentives_campaigns', labelKey: 'nav.incentives_campaigns', path: '/incentives/campaigns', icon: <Gift className="w-4 h-4" /> },
          { key: 'incentives_catalog', labelKey: 'nav.incentives_catalog', path: '/incentives/catalog', icon: <Gift className="w-4 h-4" /> },
        ]
      : []),
    ...(showIncentivesAny
      ? [{ key: 'incentives_promotions', labelKey: 'nav.incentives_promotions', path: '/incentives/promotions', icon: <Tag className="w-4 h-4" /> }]
      : []),
    { key: 'settings', labelKey: 'nav.settings', path: '/settings', icon: <Settings className="w-4 h-4" /> },
  ]
  const pageQuery = debouncedQuery.trim().toLowerCase()
  const pageMatches =
    pageQuery.length >= 1
      ? STATIC_PAGES.filter((p) =>
          [t(p.labelKey), tEn(p.labelKey)].some((l) => l.toLowerCase().includes(pageQuery)),
        )
      : []

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, close])

  const handleSelect = (result: SearchResult) => {
    addRecentSearch(result.title)
    close()
    navigate(result.url)
  }

  const handleRecentClick = (term: string) => {
    setQuery(term)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(Math.min(selectedIndex + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(Math.max(selectedIndex - 1, -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] pointer-events-none">
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={close} />
      <div className="relative w-full max-w-lg mx-4 bg-base-100 rounded-xl shadow-2xl border border-base-300 overflow-hidden pointer-events-auto">
        <div className="flex items-center gap-3 px-4 border-b border-base-300 pt-0">
          <Search className="w-5 h-5 text-base-content/40 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
            role="combobox"
            aria-expanded={results.length > 0 || recentSearches.length > 0}
            aria-autocomplete="list"
            aria-controls="command-palette-results"
            className="flex-1 py-4 bg-transparent outline-none text-base-content placeholder:text-base-content/40"
          />
          {query && (
            <Button
              variant="ghost"
              size="xs"
              shape="circle"
              onClick={() => setQuery('')}
              type="button"
              aria-label={t('labels.clear_search')}
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {isLoading && query.length >= 2 ? (
            <div className="p-4 text-center text-base-content/60">
              <Spinner size="sm" />
            </div>
          ) : pageMatches.length > 0 || results.length > 0 ? (
            <>
            {pageMatches.length > 0 && (
              <div className="py-2 border-b border-base-300" role="group" aria-label={t('search.pages')}>
                <p className="px-4 py-1 text-xs font-medium text-base-content/40 uppercase">{t('search.pages')}</p>
                {pageMatches.map((p) => (
                  <button
                    key={p.key}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-base-200 transition-colors"
                    onClick={() => { close(); navigate(p.path) }}
                    type="button"
                  >
                    <span className="text-base-content/60">{p.icon}</span>
                    <span className="font-medium">{t(p.labelKey)}</span>
                  </button>
                ))}
              </div>
            )}
            {results.length > 0 && (
            <ul role="listbox" id="command-palette-results" className="py-2">
              {results.map((result, index) => (
                <li key={`${result.type}-${result.id}`} role="option" aria-selected={index === selectedIndex}>
                  <button
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      index === selectedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-base-200'
                    }`}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    type="button"
                  >
                    <span className="text-base-content/60">{typeIcons[result.type]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-xs text-base-content/60 truncate">{result.subtitle}</p>
                      )}
                    </div>
                    <span className="text-xs text-base-content/40 capitalize">
                      {t(`search.categories.${result.type}` as const)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            )}
            </>
          ) : query.length >= 2 ? (
            <div className="p-8 text-center text-base-content/60">
              {t('search.no_results')}
            </div>
          ) : recentSearches.length > 0 ? (
            <div className="py-2">
              <p className="px-4 py-2 text-xs font-medium text-base-content/40 uppercase">
                {t('search.recent_searches')}
              </p>
              {recentSearches.map((term) => (
                <button
                  key={term}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-base-200"
                  onClick={() => handleRecentClick(term)}
                  type="button"
                >
                  <Clock className="w-4 h-4 text-base-content/40" />
                  <span>{term}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="px-4 py-2 border-t border-base-300 flex items-center gap-4 text-xs text-base-content/40">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  )
}